import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Navigation from "@/components/Navigation";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <div className="pt-28 pb-16 px-6 flex items-center justify-center">
        <Card className="w-full max-w-md rounded-2xl shadow-floating border-0" data-testid="login-card">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <Heart className="h-8 w-8 text-[#4FB3D4] mx-auto mb-3" />
              <h1 className="font-heading text-2xl font-bold text-[#1A2A3A]" data-testid="login-title">Welcome Back</h1>
              <p className="text-sm text-[#1A2A3A]/60">Sign in to your ThryvLife account</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1A2A3A] mb-1">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" placeholder="you@example.com" data-testid="login-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2A3A] mb-1">Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" placeholder="Your password" data-testid="login-password" />
              </div>
              {error && <p className="text-red-500 text-sm" data-testid="login-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full py-5 font-semibold" data-testid="login-submit">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
              </Button>
            </form>
            <p className="text-center text-sm text-[#1A2A3A]/60 mt-5">
              New here? <Link to="/register" className="text-[#4FB3D4] font-medium hover:underline" data-testid="register-link">Create an account</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
